/**
 * The ero Subscribe page builder: product-page style picker (photos left, choices right).
 * Every flavour is rendered on the page; a flavour tile shows that flavour's photos, title, sizes and
 * purchase box in place. A size card updates that flavour's form variant and the purchase box prices.
 * The purchase box (<ero-purchase-options>, shared with the product page) owns "Subscribe" vs
 * "One-time" and the hidden selling_plan input, which is submitted with the flavour's form along with
 * the quantity from the +/- box.
 */
class EroSubscribePicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('submit', this.#onSubmit);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const flavour = event.target.closest('[data-flavour-target]');
    if (flavour instanceof HTMLElement && this.contains(flavour)) {
      this.#showFlavour(flavour);
      return;
    }

    const size = event.target.closest('.ero-size[data-variant-id]');
    if (size instanceof HTMLButtonElement && this.contains(size)) {
      this.#selectSize(size);
      return;
    }

    const thumb = event.target.closest('.ero-subpick__thumb');
    if (thumb instanceof HTMLButtonElement && this.contains(thumb)) {
      this.#showPhoto(thumb);
      return;
    }

    const step = event.target.closest('.ero-subpick__qty-button');
    if (step instanceof HTMLButtonElement && this.contains(step)) this.#stepQuantity(step);
  };

  /** Keeps a typed quantity within its limits. @param {Event} event */
  #onChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('.ero-subpick__qty-input')) {
      event.target.value = String(this.#clampQuantity(event.target, Number(event.target.value)));
    }
  };

  /** @param {HTMLInputElement} input @param {number} value */
  #clampQuantity(input, value) {
    const min = Number(input.min) || 1;
    const max = Number(input.max) || 99;
    return Math.min(max, Math.max(min, Number.isFinite(value) ? Math.round(value) : min));
  }

  /** @param {HTMLButtonElement} button */
  #stepQuantity(button) {
    const input = button.closest('.ero-subpick__qty')?.querySelector('.ero-subpick__qty-input');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = String(this.#clampQuantity(input, Number(input.value) + Number(button.dataset.step)));
  }

  /** @param {HTMLElement} tile */
  #showFlavour(tile) {
    const handle = tile.dataset.flavourTarget;
    if (!handle || tile.getAttribute('aria-pressed') === 'true') return;

    // Carry "Subscribe" vs "One-time" and the delivery rhythm over to the next flavour.
    const previous = this.#visibleBuy();
    const choice = previous?.querySelector('.ero-purchase__radio:checked');
    const plan = previous?.querySelector('.ero-purchase__plans');

    for (const other of this.querySelectorAll('[data-flavour-target]')) {
      const isCurrent = other === tile;
      other.setAttribute('aria-pressed', String(isCurrent));
      other.classList.toggle('is-current', isCurrent);
    }
    for (const part of this.querySelectorAll('[data-flavour]')) {
      if (part instanceof HTMLElement) part.hidden = part.dataset.flavour !== handle;
    }

    const next = this.#visibleBuy();
    const widget = next?.querySelector('ero-purchase-options');
    if (widget && choice instanceof HTMLInputElement) {
      const radio = widget.querySelector(`.ero-purchase__radio[value="${CSS.escape(choice.value)}"]`);
      if (radio instanceof HTMLInputElement) radio.checked = true;
      const nextPlans = widget.querySelector('.ero-purchase__plans');
      if (plan instanceof HTMLSelectElement && nextPlans instanceof HTMLSelectElement) nextPlans.value = plan.value;
      widget.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @returns {HTMLElement | null} */
  #visibleBuy() {
    return this.querySelector('.ero-subpick__buy:not([hidden])');
  }

  /** @param {HTMLButtonElement} size */
  #selectSize(size) {
    const buy = size.closest('.ero-subpick__buy');
    if (!buy) return;

    for (const other of buy.querySelectorAll('.ero-size[data-variant-id]')) {
      const isCurrent = other === size;
      other.setAttribute('aria-pressed', String(isCurrent));
      other.classList.toggle('is-current', isCurrent);
    }

    const variant = buy.querySelector('.ero-subpick__variant');
    if (variant instanceof HTMLInputElement) variant.value = size.dataset.variantId ?? '';

    const prices = { compare: size.dataset.compare, subscription: size.dataset.subscription, once: size.dataset.price };
    for (const [key, value] of Object.entries(prices)) {
      const target = buy.querySelector(`[data-ero-price="${key}"]`);
      if (target && value) target.textContent = value;
    }
  }

  /** @param {HTMLButtonElement} thumb */
  #showPhoto(thumb) {
    const gallery = thumb.closest('.ero-subpick__gallery');
    const image = gallery?.querySelector('.ero-subpick__main img');
    if (!(image instanceof HTMLImageElement)) return;

    if (thumb.dataset.srcset) image.srcset = thumb.dataset.srcset;
    if (thumb.dataset.src) image.src = thumb.dataset.src;
    for (const other of gallery?.querySelectorAll('.ero-subpick__thumb') || []) {
      const isCurrent = other === thumb;
      other.setAttribute('aria-pressed', String(isCurrent));
      other.classList.toggle('is-current', isCurrent);
    }
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('ero-subpick__form')) return;
    event.preventDefault();

    const button = form.querySelector('.ero-subpick__submit');
    const labelElement = form.querySelector('.ero-subpick__submit-label');
    const error = form.querySelector('.ero-subpick__error');
    if (!(button instanceof HTMLButtonElement) || !labelElement) return;

    const label = labelElement.textContent;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    labelElement.textContent = this.dataset.addingText ?? label;
    if (error instanceof HTMLElement) error.hidden = true;

    try {
      // FormData includes the purchase box's selling_plan input (linked by its form attribute) when enabled.
      const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

      // Open the cart drawer in place when the Rebuy Smart Cart is installed; otherwise go to the cart page.
      const rebuy = /** @type {any} */ (window).Rebuy;
      if (rebuy?.SmartCart?.show) {
        await rebuy.Cart?.fetchCart?.();
        // Rebuy reacts to the add on its own first; open the drawer once it has settled.
        setTimeout(() => rebuy.SmartCart.show(), 400);
      } else {
        window.location.href = this.dataset.cartUrl ?? '/cart';
        return;
      }
    } catch {
      if (error instanceof HTMLElement) {
        error.textContent = this.dataset.errorText ?? '';
        error.hidden = false;
      }
    }

    button.removeAttribute('aria-busy');
    button.disabled = false;
    labelElement.textContent = label;
  };
}

if (!customElements.get('ero-subscribe-picker')) {
  customElements.define('ero-subscribe-picker', EroSubscribePicker);
}
